import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Upload, Search, Filter, Grid, List, 
  Folder, File, Image, Video, Archive, Code,
  Download, Share2, Edit, Trash2, Eye, MoreHorizontal,
  Calendar, User, Clock, Star, Bookmark, MessageSquare,
  Plus, FolderPlus, FilePlus, CheckCircle, AlertTriangle,
  Lock, Unlock, Users, EyeOff, DownloadCloud
} from 'lucide-react';

const Documents = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sortBy, setSortBy] = useState('name');

  // Comprehensive mock data
  const folders = [
    { id: 1, name: 'Project Plans', count: 24, color: 'bg-blue-500' },
    { id: 2, name: 'Architectural Drawings', count: 156, color: 'bg-green-500' },
    { id: 3, name: 'Engineering Specs', count: 89, color: 'bg-purple-500' },
    { id: 4, name: 'Contracts & Legal', count: 12, color: 'bg-red-500' },
    { id: 5, name: 'Financial Documents', count: 67, color: 'bg-yellow-500' },
    { id: 6, name: 'Photos & Media', count: 234, color: 'bg-pink-500' },
    { id: 7, name: 'Reports & Analytics', count: 45, color: 'bg-indigo-500' },
    { id: 8, name: 'Team Resources', count: 78, color: 'bg-orange-500' }
  ];

  const documents = [
    {
      id: 1,
      name: 'Riyadh Mall - Master Plan.pdf',
      type: 'pdf',
      size: '2.4 MB',
      folder: 'Project Plans',
      folderId: 1,
      uploadedBy: 'Ahmed Al-Rashid',
      uploadDate: '2024-01-15',
      lastModified: '2024-03-20',
      version: '2.1',
      status: 'approved',
      tags: ['master-plan', 'approved', 'final'],
      description: 'Comprehensive master plan for Riyadh Mall construction project',
      permissions: 'team',
      downloads: 45,
      views: 128,
      starred: true
    },
    {
      id: 2,
      name: 'Structural Analysis Report.docx',
      type: 'docx',
      size: '1.8 MB',
      folder: 'Engineering Specs',
      folderId: 3,
      uploadedBy: 'Sarah Johnson',
      uploadDate: '2024-02-10',
      lastModified: '2024-03-15',
      version: '1.3',
      status: 'review',
      tags: ['structural', 'analysis', 'engineering'],
      description: 'Detailed structural analysis for main building components',
      permissions: 'private',
      downloads: 23,
      views: 67,
      starred: false
    },
    {
      id: 3,
      name: 'Site Photos - March 2024.zip',
      type: 'zip',
      size: '45.2 MB',
      folder: 'Photos & Media',
      folderId: 6,
      uploadedBy: 'Mohammed Hassan',
      uploadDate: '2024-03-25',
      lastModified: '2024-03-25',
      version: '1.0',
      status: 'pending',
      tags: ['photos', 'site', 'progress'],
      description: 'Site progress photos from March 2024',
      permissions: 'public',
      downloads: 12,
      views: 34,
      starred: false
    },
    {
      id: 4,
      name: 'Contract - Main Contractor.pdf',
      type: 'pdf',
      size: '8.7 MB',
      folder: 'Contracts & Legal',
      folderId: 4,
      uploadedBy: 'Legal Team',
      uploadDate: '2024-01-05',
      lastModified: '2024-02-28',
      version: '3.2',
      status: 'approved',
      tags: ['contract', 'legal', 'approved'],
      description: 'Main contractor agreement and terms',
      permissions: 'restricted',
      downloads: 8,
      views: 15,
      starred: true
    },
    {
      id: 5,
      name: 'Budget Analysis Q1 2024.xlsx',
      type: 'xlsx',
      size: '3.2 MB',
      folder: 'Financial Documents',
      folderId: 5,
      uploadedBy: 'Finance Team',
      uploadDate: '2024-04-01',
      lastModified: '2024-04-05',
      version: '1.1',
      status: 'approved',
      tags: ['budget', 'financial', 'q1'],
      description: 'First quarter budget analysis and projections',
      permissions: 'team',
      downloads: 31,
      views: 89,
      starred: false
    },
    {
      id: 6,
      name: 'Architectural Renderings.jpg',
      type: 'jpg',
      size: '12.5 MB',
      folder: 'Architectural Drawings',
      folderId: 2,
      uploadedBy: 'Fatima Al-Zahra',
      uploadDate: '2024-02-20',
      lastModified: '2024-03-10',
      version: '2.0',
      status: 'approved',
      tags: ['renderings', 'architecture', 'visual'],
      description: 'High-resolution architectural renderings',
      permissions: 'public',
      downloads: 67,
      views: 156,
      starred: true
    },
    {
      id: 7,
      name: 'Safety Protocols Manual.pdf',
      type: 'pdf',
      size: '4.1 MB',
      folder: 'Team Resources',
      folderId: 8,
      uploadedBy: 'Safety Officer',
      uploadDate: '2024-01-20',
      lastModified: '2024-03-01',
      version: '1.5',
      status: 'approved',
      tags: ['safety', 'protocols', 'manual'],
      description: 'Comprehensive safety protocols for construction site',
      permissions: 'team',
      downloads: 89,
      views: 234,
      starred: false
    },
    {
      id: 8,
      name: 'Progress Report - Week 12.pptx',
      type: 'pptx',
      size: '6.8 MB',
      folder: 'Reports & Analytics',
      folderId: 7,
      uploadedBy: 'Project Manager',
      uploadDate: '2024-03-22',
      lastModified: '2024-03-22',
      version: '1.0',
      status: 'draft',
      tags: ['report', 'progress', 'weekly'],
      description: 'Weekly progress report for week 12',
      permissions: 'team',
      downloads: 18,
      views: 45,
      starred: false
    },
    {
      id: 9,
      name: 'Material Specifications.csv',
      type: 'csv',
      size: '0.8 MB',
      folder: 'Engineering Specs',
      folderId: 3,
      uploadedBy: 'David Chen',
      uploadDate: '2024-02-15',
      lastModified: '2024-03-05',
      version: '1.2',
      status: 'approved',
      tags: ['materials', 'specifications', 'engineering'],
      description: 'Detailed material specifications and requirements',
      permissions: 'team',
      downloads: 42,
      views: 98,
      starred: false
    },
    {
      id: 10,
      name: 'Site Survey Results.pdf',
      type: 'pdf',
      size: '15.3 MB',
      folder: 'Project Plans',
      folderId: 1,
      uploadedBy: 'Survey Team',
      uploadDate: '2024-01-10',
      lastModified: '2024-01-10',
      version: '1.0',
      status: 'approved',
      tags: ['survey', 'site', 'analysis'],
      description: 'Comprehensive site survey and analysis results',
      permissions: 'team',
      downloads: 28,
      views: 76,
      starred: true
    }
  ];

  const filters = [
    { id: 'all', label: 'All Files', count: documents.length },
    { id: 'pdf', label: 'PDF Files', count: documents.filter(d => d.type === 'pdf').length },
    { id: 'images', label: 'Images', count: documents.filter(d => ['jpg', 'png', 'gif'].includes(d.type)).length },
    { id: 'documents', label: 'Documents', count: documents.filter(d => ['docx', 'xlsx', 'pptx'].includes(d.type)).length },
    { id: 'archives', label: 'Archives', count: documents.filter(d => ['zip', 'rar'].includes(d.type)).length }
  ];

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return FileText;
      case 'docx': return FileText;
      case 'xlsx': return FileText;
      case 'pptx': return FileText;
      case 'jpg':
      case 'png':
      case 'gif': return Image;
      case 'mp4':
      case 'avi': return Video;
      case 'zip':
      case 'rar': return Archive;
      case 'csv': return Code;
      default: return File;
    }
  };

  const getFileColor = (type) => {
    switch (type) {
      case 'pdf': return 'text-red-500';
      case 'docx': return 'text-blue-500';
      case 'xlsx': return 'text-green-500';
      case 'pptx': return 'text-orange-500';
      case 'jpg':
      case 'png':
      case 'gif': return 'text-purple-500';
      case 'mp4':
      case 'avi': return 'text-pink-500';
      case 'zip':
      case 'rar': return 'text-yellow-500';
      case 'csv': return 'text-indigo-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPermissionIcon = (permissions) => {
    switch (permissions) {
      case 'public': return Unlock;
      case 'team': return Users;
      case 'private': return Lock;
      case 'restricted': return EyeOff;
      default: return Unlock;
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'pdf' && doc.type === 'pdf') ||
                         (selectedFilter === 'images' && ['jpg', 'png', 'gif'].includes(doc.type)) ||
                         (selectedFilter === 'documents' && ['docx', 'xlsx', 'pptx'].includes(doc.type)) ||
                         (selectedFilter === 'archives' && ['zip', 'rar'].includes(doc.type));
    const matchesFolder = selectedFolder === 'all' || doc.folderId === parseInt(selectedFolder);
    return matchesSearch && matchesFilter && matchesFolder;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    switch (sortBy) {
      case 'name': return a.name.localeCompare(b.name);
      case 'date': return new Date(b.lastModified) - new Date(a.lastModified);
      case 'size': return parseFloat(a.size) - parseFloat(b.size);
      case 'type': return a.type.localeCompare(b.type);
      default: return 0;
    }
  });

  const DocumentCard = ({ document }) => {
    const FileIcon = getFileIcon(document.type);
    const PermissionIcon = getPermissionIcon(document.permissions);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -5 }}
        className={`p-4 rounded-xl backdrop-blur-sm border ${
          isDarkMode 
            ? 'bg-white/10 border-white/20' 
            : 'bg-white/70 border-gray-200/50'
        } shadow-lg hover:shadow-xl transition-all duration-300`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
              <FileIcon className={`w-5 h-5 ${getFileColor(document.type)}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`font-semibold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {document.name}
              </h4>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
                {document.description}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {document.starred && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
            <PermissionIcon className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <button className={`p-1 rounded transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {document.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className={`px-2 py-1 text-xs rounded-full ${
                isDarkMode 
                  ? 'bg-white/10 text-gray-300' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {tag}
            </span>
          ))}
          {document.tags.length > 3 && (
            <span className={`px-2 py-1 text-xs rounded-full ${
              isDarkMode 
                ? 'bg-white/10 text-gray-300' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              +{document.tags.length - 3}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Size: {document.size}
          </div>
          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Version: {document.version}
          </div>
          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Downloads: {document.downloads}
          </div>
          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Views: {document.views}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
            {document.status}
          </div>
          <div className="flex items-center space-x-2">
            <button className={`p-1 rounded transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <Eye className="w-4 h-4" />
            </button>
            <button className={`p-1 rounded transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <Download className="w-4 h-4" />
            </button>
            <button className={`p-1 rounded transition-all ${
              isDarkMode 
                ? 'hover:bg-white/10 text-gray-300' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}>
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  const FolderCard = ({ folder }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className={`p-4 rounded-xl backdrop-blur-sm border cursor-pointer ${
        isDarkMode 
          ? 'bg-white/10 border-white/20 hover:bg-white/20' 
          : 'bg-white/70 border-gray-200/50 hover:bg-white/90'
      } shadow-lg hover:shadow-xl transition-all duration-300`}
      onClick={() => setSelectedFolder(folder.id.toString())}
    >
      <div className="flex items-center space-x-3">
        <div className={`p-3 rounded-lg ${folder.color}`}>
          <Folder className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {folder.name}
          </h4>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {folder.count} files
          </p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Documents
            </h1>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              File management, version control, and approval workflows
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Files</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-lg"
            >
              <FolderPlus className="w-4 h-4" />
              <span>New Folder</span>
            </motion.button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border transition-all ${
                isDarkMode 
                  ? 'bg-white/10 border-white/20 text-white placeholder-gray-400' 
                  : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                  selectedFilter === filter.id
                    ? isDarkMode
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                    : isDarkMode
                      ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                      : 'bg-white/80 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'grid'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'list'
                  ? isDarkMode
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                    : 'bg-white/80 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Folders Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Folders
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <AnimatePresence>
            {folders.map((folder, index) => (
              <FolderCard key={folder.id} folder={folder} />
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Documents Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Documents {selectedFolder !== 'all' && `(${folders.find(f => f.id.toString() === selectedFolder)?.name})`}
          </h2>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-3 py-1 rounded-lg text-sm border transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white' 
                : 'bg-white/80 border-gray-200 text-gray-900'
            } focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
          >
            <option value="name">Sort by Name</option>
            <option value="date">Sort by Date</option>
            <option value="size">Sort by Size</option>
            <option value="type">Sort by Type</option>
          </select>
        </div>

        {/* Documents Grid */}
        <div className={`grid gap-4 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
            : 'grid-cols-1'
        }`}>
          <AnimatePresence>
            {sortedDocuments.map((document, index) => (
              <DocumentCard key={document.id} document={document} />
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {sortedDocuments.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <FileText className={`w-16 h-16 mx-auto mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-300'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No documents found
            </h3>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Try adjusting your search or filter criteria
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Documents; 